import apiService from "./api";

export type Gender = "MALE" | "FEMALE" | "OTHER";

export type MaritalStatus =
	| "SINGLE"
	| "MARRIED"
	| "STABLE_UNION"
	| "DIVORCED"
	| "WIDOWED";

export const GENDERS: Gender[] = ["MALE", "FEMALE", "OTHER"];

export const MARITAL_STATUSES: MaritalStatus[] = [
	"SINGLE",
	"MARRIED",
	"STABLE_UNION",
	"DIVORCED",
	"WIDOWED",
];

/**
 * Os oito tipos do sistema ABO/Rh. Campo livre aceitava "O positivo", "o+" e
 * "0+" (zero) para a mesma coisa — dado que não dá para filtrar nem conferir
 * numa emergência. Fica `String?` no banco: "A+" não é identificador válido de
 * enum do Prisma, e a lista aqui já fecha a entrada.
 */
/** UFs brasileiras. O select fecha a entrada; o DTO exige 2 letras maiúsculas. */
export const BRAZILIAN_STATES = [
	"AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
	"MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
	"RS", "RO", "RR", "SC", "SP", "SE", "TO",
] as const;

export const BLOOD_TYPES = [
	"A+",
	"A-",
	"B+",
	"B-",
	"AB+",
	"AB-",
	"O+",
	"O-",
] as const;

export interface Person {
	id: string;
	code: string;
	firstName: string;
	lastName: string;
	nickname?: string | null;
	birthDate?: string | null;
	rg?: string | null;
	cpf?: string | null;
	/** Logradouro. Fichas antigas trazem o endereço inteiro aqui (ISSUE-032) */
	address?: string | null;
	addressNumber?: string | null;
	addressComplement?: string | null;
	neighborhood?: string | null;
	city?: string | null;
	state?: string | null;
	/** Só os 8 dígitos — a máscara é da tela */
	zipCode?: string | null;
	phone?: string | null;
	mobile?: string | null;
	email?: string | null;
	/** Consentimento para receber alertas por WhatsApp (FEAT-026) */
	whatsappOptIn?: boolean;
	whatsappOptInAt?: string | null;
	// Ficha completa (ISSUE-029)
	fatherName?: string | null;
	motherName?: string | null;
	nationality?: string | null;
	gender?: Gender | null;
	maritalStatus?: MaritalStatus | null;
	occupation?: string | null;
	emergencyContactName?: string | null;
	emergencyContactPhone?: string | null;
	/**
	 * Saúde é dado sensível (LGPD): o backend só devolve estes dois campos para
	 * OWNER/ADMIN. Para os demais eles vêm ausentes — não vazios.
	 */
	bloodType?: string | null;
	healthNotes?: string | null;
	/** Autorização de uso de imagem — consentimento datado */
	imageUseConsent?: boolean;
	imageUseConsentAt?: string | null;
	notes?: string | null;
	photoUrl?: string | null;
	memberType?: 'CONSULENTE' | 'MEDIUM';
	/** Arquivamento (BUG-056): pessoa fora das listas, com o histórico preservado. */
	archivedAt?: string | null;
	archiveReason?: string | null;
	archivedBy?: { id: string; account: { displayName: string } } | null;
	memberSince?: string | null;
	firstVisitDate?: string | null;
	paiDeCabecaId?: string | null;
	maeDeCabecaId?: string | null;
	paiDeCabeca?: { id: string; name: string; color?: string | null } | null;
	maeDeCabeca?: { id: string; name: string; color?: string | null } | null;
	createdAt: string;
	updatedAt: string;
	createdById?: string;
	membersAssignments?: Array<{
		id: string;
		personId: string;
		journeyStageId: string;
		since: string;
		endedAt?: string | null;
		isActive: boolean;
		journeyStage: {
			id: string;
			name: string;
			order: number;
		};
	}>;
}

export interface BirthdayPerson {
	id: string;
	firstName: string;
	lastName: string;
	nickname?: string | null;
	birthDate: string;
	daysUntilBirthday: number;
	age: number;
	isToday: boolean;
}

export interface PersonFilter {
	search?: string;
	page?: number;
	limit?: number;
	orderBy?: string;
	order?: "asc" | "desc";
	firstName?: string;
	lastName?: string;
	nickname?: string;
	email?: string;
	withoutActiveJourney?: boolean;
	hasActiveTickets?: boolean;
	recentVisitDays?: number;
	hasActiveJourney?: boolean;
	journeyStageId?: string;
	memberType?: 'CONSULENTE' | 'MEDIUM';
	mode?: 'full' | 'select';
}

export interface PaginatedResponse<T> {
	data: T[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

// Person CRUD operations
export const getPersons = async (
	filter: PersonFilter = {}
): Promise<PaginatedResponse<Person>> => {
	const params: any = { ...filter };

	try {
		const response = await apiService.get("/persons", { params });

		if (response) {
			return response;
		} else {
			console.warn("Resposta inválida ao buscar pessoas");
			return {
				data: [],
				meta: {
					total: 0,
					page: Number(params.page) || 1,
					limit: Number(params.limit) || 10,
					pages: 0,
				},
			};
		}
	} catch (error) {
		console.error("Erro na requisição getPersons:", error);
		return {
			data: [],
			meta: {
				total: 0,
				page: Number(params.page) || 1,
				limit: Number(params.limit) || 10,
				pages: 0,
			},
		};
	}
};

export const getPersonById = async (id: string): Promise<Person> => {
	const response = await apiService.get(`/persons/${id}`);
	return response;
};

export const createPerson = async (
	person: Partial<Person>
): Promise<Person> => {
	const response = await apiService.post("/persons", person);
	return response;
};

/**
 * Cadastra a pessoa JÁ dentro da corrente (ISSUE-030).
 *
 * Uma requisição só, e uma transação só no backend: criar a pessoa aqui e
 * chamar `becomeMedium` em seguida deixaria um consulente órfão sempre que o
 * segundo passo falhasse.
 */
export const createMedium = async (
	payload: Partial<Person> & { date: string; entryNotes?: string }
): Promise<Person> => {
	const response = await apiService.post("/persons/medium", payload);
	return response;
};

export const updatePerson = async (
	id: string,
	person: Partial<Person>
): Promise<Person> => {
	const response = await apiService.patch(`/persons/${id}`, person);
	return response;
};

export const deletePerson = async (id: string): Promise<void> => {
	await apiService.delete(`/persons/${id}`);
};

/**
 * O que se perde ao excluir esta pessoa (BUG-056, camada 2).
 *
 * A tela pede isto ANTES de confirmar. Antes, quem apertava "excluir" decidia no
 * escuro: a lista de itens no diálogo era estática e igual para todo mundo, e a
 * dívida em aberto só aparecia depois — pendurada, sem dono.
 */
export interface DeletionImpact {
	person: { id: string; firstName: string; lastName: string };
	archived: boolean;
	openCharges: { count: number; total: number };
	paidMovements: { count: number; total: number };
	totalMovements: number;
	activeSubscriptions: number;
	hasActiveMandate: boolean;
	tickets: number;
	messages: number;
	canDelete: boolean;
	blockedReason: 'FINANCIAL_HISTORY' | 'ACTIVE_MANDATE' | null;
}

export const getDeletionImpact = async (id: string): Promise<DeletionImpact> => {
	return apiService.get(`/persons/${id}/deletion-impact`);
};

export const archivePerson = async (id: string, reason?: string): Promise<Person> => {
	return apiService.post(`/persons/${id}/archive`, reason ? { reason } : {});
};

export const unarchivePerson = async (id: string): Promise<Person> => {
	return apiService.post(`/persons/${id}/unarchive`, {});
};

export const uploadPersonPhoto = async (
	personId: string,
	file: File
): Promise<Person> => {
	const formData = new FormData();
	formData.append("photo", file);
	const response = await apiService.post(`/persons/${personId}/photo`, formData, {
		headers: { "Content-Type": undefined },
	});
	return response;
};

export const deletePersonPhoto = async (personId: string): Promise<Person> => {
	const response = await apiService.delete(`/persons/${personId}/photo`);
	return response;
};

export const getUpcomingBirthdays = async (
	days: number = 14
): Promise<BirthdayPerson[]> => {
	try {
		const response = await apiService.get("/persons/birthdays/upcoming", {
			params: { days },
		});
		return response || [];
	} catch (error) {
		console.error("Erro ao buscar aniversariantes:", error);
		return [];
	}
};
